import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { translateMessage, getLanguageInfo } from '../lib/messageTranslation';
import { notifyNewMessage } from '../lib/notificationTriggers';
import toast from 'react-hot-toast';
import { 
  FaPaperPlane, FaArrowLeft, FaUser, FaSpinner, FaCheck, FaCheckDouble, 
  FaSearch, FaTimes, FaLanguage, FaGlobe, FaComments, FaUserFriends, FaCircle
} from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';

interface Conversation {
  friend_id: string;
  friend_email: string;
  friend_full_name: string | null;
  friend_profile_image: string | null;
  friend_is_online: boolean;
  last_message: string;
  last_message_time: string;
  last_message_sender: string;
  unread_count: number;
}

interface PrivateMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  original_language: 'english' | 'french' | 'haitian_creole';
  spanish_translation: string | null;
  is_read: boolean;
  created_at: string;
  sender_email?: string;
  sender_profile_image?: string;
  sender_full_name?: string;
}

interface Friend {
  id: string;
  email: string;
  profile_image: string | null;
  full_name: string | null;
  is_online: boolean;
}

export default function Messages() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<PrivateMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'french' | 'haitian_creole'>('english');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUser();

  // Get friend ID from navigation state if coming from Learners/Friends page
  const initialFriendId = location.state?.friendId as string | undefined;

  // Typing effect for search
  useEffect(() => {
    if (!searchQuery) { setTypingText(''); return; }
    let i = 0;
    const id = setInterval(() => {
      setTypingText(searchQuery.slice(0, i));
      i++;
      if (i > searchQuery.length) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [searchQuery]);

  // Mobile resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check(); window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Require auth, then load convos
  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    checkAccess();
    loadConversations();
    
    // If coming from Learners/Friends page with a specific friend, select them
    if (initialFriendId && conversations.length > 0) {
      const targetConversation = conversations.find(c => c.friend_id === initialFriendId);
      if (targetConversation) {
        setSelectedConversation(targetConversation);
        if (isMobile) setShowConversationList(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, initialFriendId, conversations.length]);

  const checkAccess = async () => {
    if (!currentUser) {
      setCheckingAccess(false);
      return;
    }

    try {
      // Check if user is admin
      if (currentUser.is_admin) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      // Check for valid coupon usage
      const { data: couponUsage, error } = await supabase
        .from('stripecouponusage')
        .select('*')
        .eq('user_id', currentUser.id)
        .gte('valid_until', new Date().toISOString());

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setHasAccess(!!couponUsage && couponUsage.length > 0);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  // Filter conversations
  useEffect(() => {
    if (!searchQuery.trim()) { setFilteredConversations(conversations); return; }
    const q = searchQuery.toLowerCase();
    setFilteredConversations(
      conversations.filter(
        c =>
          c.friend_email.toLowerCase().includes(q) ||
          (c.friend_full_name && c.friend_full_name.toLowerCase().includes(q)
        )
      )
    );
  }, [conversations, searchQuery]);

  // Scroll down when messages change
  useEffect(() => { scrollToBottom(); }, [messages]);

  // ===== Realtime: conversations list =====
  useEffect(() => {
    if (!currentUser?.id) return;

    const conversationsChannel = supabase
      .channel(`conversations-${currentUser.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'private_messages' },
        () => { loadConversations(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(conversationsChannel); };
  }, [currentUser?.id]); // eslint-disable-line

  // ===== Realtime: messages in the open thread =====
  useEffect(() => {
    if (!selectedConversation?.friend_id || !currentUser?.id) return;

    // Load existing messages once when opening a thread
    loadMessages(selectedConversation.friend_id);

    // Subscribe table-wide and filter in code (robust across configs)
    const channel = supabase
      .channel(`pm-${currentUser.id}-${selectedConversation.friend_id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'private_messages' },
        async (payload) => {
          const message = payload.new;
          
          // Create notification for receiver if it's not from current user
          if (message.sender_id !== currentUser.id && message.receiver_id === currentUser.id) {
            // Get sender info for notification
            const { data: sender } = await supabase
              .from('stripeusers')
              .select('email, full_name')
              .eq('id', message.sender_id)
              .single();

            if (sender) {
              const senderName = sender.full_name || sender.email;
              await notifyNewMessage(currentUser.id, message.sender_id, senderName);
            }
          }

          const m = message as PrivateMessage;

          const isThisThread =
            (m.sender_id === currentUser.id && m.receiver_id === selectedConversation.friend_id) ||
            (m.sender_id === selectedConversation.friend_id && m.receiver_id === currentUser.id);

          if (!isThisThread) return;

          // Enrich sender info
          const { data: senderData } = await supabase
            .from('stripeusers')
            .select('email, profile_image, full_name')
            .eq('id', m.sender_id)
            .single();

          const enriched: PrivateMessage = {
            ...m,
            sender_email: senderData?.email || 'Unknown',
            sender_profile_image: senderData?.profile_image || null,
            sender_full_name: senderData?.full_name || null,
          };

          setMessages(prev => [...prev, enriched]);
          scrollToBottom();

          if (m.sender_id === selectedConversation.friend_id) {
            await markMessageAsRead(m.id);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'private_messages' },
        (payload) => {
          const m = payload.new as PrivateMessage;
          const isThisThread =
            (m.sender_id === currentUser.id && m.receiver_id === selectedConversation.friend_id) ||
            (m.sender_id === selectedConversation.friend_id && m.receiver_id === currentUser.id);
          if (!isThisThread) return;

          setMessages(prev => prev.map(pm => (pm.id === m.id ? { ...pm, ...m } as PrivateMessage : pm)));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selectedConversation?.friend_id, currentUser?.id]); // eslint-disable-line

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  // ===== Data loads =====
  const loadConversations = async () => {
    if (!currentUser?.id) return;
    try {
      const { data: friendships, error: friendshipsError } = await supabase
        .from('user_friendships')
        .select('requester_id, addressee_id, status')
        .or(`requester_id.eq.${currentUser.id},addressee_id.eq.${currentUser.id}`)
        .eq('status', 'accepted');
      if (friendshipsError) throw friendshipsError;

      if (!friendships?.length) { setConversations([]); if (loading) setLoading(false); return; }

      const friendIds = friendships
        .map(f => (f.requester_id === currentUser.id ? f.addressee_id : f.requester_id))
        .filter(Boolean) as string[];

      const { data: friends, error: friendsError } = await supabase
        .from('stripeusers')
        .select('id, email, profile_image, full_name, is_online')
        .in('id', friendIds);
      if (friendsError) throw friendsError;

      const friendsMap: Record<string, Friend> = {};
      (friends || []).forEach(f => (friendsMap[f.id] = f));

      const conversationPromises = friendIds.map(async (friendId) => {
        const { data: lastMessage } = await supabase
          .from('private_messages')
          .select('content, spanish_translation, created_at, sender_id')
          .or(
            `and(sender_id.eq.${currentUser.id},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${currentUser.id})`
          )
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const { count: unreadCount } = await supabase
          .from('private_messages')
          .select('*', { count: 'exact', head: true })
          .eq('sender_id', friendId)
          .eq('receiver_id', currentUser.id)
          .eq('is_read', false);

        const friend = friendsMap[friendId];
        return {
          friend_id: friendId,
          friend_email: friend?.email || 'Unknown',
          friend_full_name: friend?.full_name || null,
          friend_profile_image: friend?.profile_image || null,
          friend_is_online: !!friend?.is_online,
          last_message: lastMessage?.spanish_translation || lastMessage?.content || '',
          last_message_time: lastMessage?.created_at || '',
          last_message_sender: lastMessage?.sender_id || '',
          unread_count: unreadCount || 0,
        } as Conversation;
      });

      const conversationsData = await Promise.all(conversationPromises);
      conversationsData.sort((a, b) => {
        const ta = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
        const tb = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
        return tb - ta;
      });

      setConversations(conversationsData);
    } catch (e:any) {
      console.error('Error loading conversations:', e);
      toast.error('Failed to load conversations');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const loadMessages = async (friendId: string) => {
    if (!currentUser?.id) return;
    setMessagesLoading(true);
    try {
      const { data: rows, error } = await supabase
        .from('private_messages')
        .select('*')
        .or(
          `and(sender_id.eq.${currentUser.id},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${currentUser.id})`
        )
        .order('created_at', { ascending: true });
      if (error && error.code !== 'PGRST116') throw error;

      const msgs = rows || [];
      const senderIds = [...new Set(msgs.map(m => m.sender_id))];

      let sendersMap = new Map<string, Friend>();
      if (senderIds.length) {
        const { data: senders } = await supabase
          .from('stripeusers')
          .select('id, email, profile_image, full_name')
          .in('id', senderIds);
        sendersMap = new Map((senders || []).map(s => [s.id, s as unknown as Friend]));
      }

      const formatted: PrivateMessage[] = msgs.map(m => {
        const s = sendersMap.get(m.sender_id);
        return {
          ...m,
          sender_email: s?.email || 'Unknown',
          sender_profile_image: s?.profile_image || null,
          sender_full_name: s?.full_name || null,
        };
      });

      setMessages(formatted);

      const unreadIds = formatted.filter(m => m.sender_id === friendId && !m.is_read).map(m => m.id);
      if (unreadIds.length) await markMultipleMessagesAsRead(unreadIds);
    } catch (e:any) {
      console.error('Error loading messages:', e);
      toast.error('Failed to load messages');
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  // ===== Helpers =====
  const markMessageAsRead = async (messageId: string) => {
    if (!currentUser?.id) return;
    try {
      await supabase.from('private_messages').update({ is_read: true }).eq('id', messageId).eq('receiver_id', currentUser.id);
    } catch (e) { console.error('Error marking message as read:', e); }
  };

  const markMultipleMessagesAsRead = async (ids: string[]) => {
    if (!currentUser?.id || !ids.length) return;
    try {
      await supabase.from('private_messages').update({ is_read: true }).in('id', ids).eq('receiver_id', currentUser.id);
      loadConversations();
    } catch (e) { console.error('Error marking messages as read:', e); }
  };

  // ===== Send with optimistic UI =====
  const translateToSpanish = async (text: string, from: 'english' | 'french' | 'haitian_creole') => {
    setTranslating(true);
    try {
      return await translateMessage(text, from);
    } finally {
      setTranslating(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser?.id || !selectedConversation || sending) return;

    setSending(true);
    const raw = newMessage.trim();
    setNewMessage('');

    try {
      const spanish = await translateToSpanish(raw, selectedLanguage);

      // Insert and return the inserted row
      const { data: inserted, error } = await supabase
        .from('private_messages')
        .insert({
          sender_id: currentUser.id,
          receiver_id: selectedConversation.friend_id,
          content: raw,
          original_language: selectedLanguage,
          spanish_translation: spanish,
        })
        .select('*')
        .single();

      if (error) throw error;

      // Optimistic append immediately for the sender

      // Also nudge conversations list
      loadConversations();
    } catch (e:any) {
      console.error('Error sending message:', e);
      toast.error('Failed to send message');
      // rollback text if failed
      setNewMessage(raw);
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (c: Conversation) => {
    setSelectedConversation(c);
    if (isMobile) setShowConversationList(false);
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const now = new Date();
    const diffH = (now.getTime() - d.getTime()) / (1000 * 60 * 60);
    if (diffH < 24) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffH < 168) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const totalUnread = useMemo(
    () => conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0),
    [conversations]
  );

  const getLanguageFlag = (language: string) => getLanguageInfo(language).flag;
  const getLanguageLabel = (language: string) => getLanguageInfo(language).label;

  // Show loading screen while checking access
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Checking access...</p>
        </div>
      </div>
    );
  }

  // Show access required screen if user doesn't have access
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Stars */}
          {[...Array(200)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 border-b border-white/10">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden border-4 border-yellow-400/50">
                  <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
                    <FaComments className="w-10 h-10 text-black" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Messages Access Required</h2>
                <p className="text-white/80">Purchase a coupon to chat with other learners</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">What you'll get:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">Chat with other language learners</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">Automatic Spanish translation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">Access to all songs and stories</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">30 days of unlimited access</span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-400/20 border border-yellow-400/30 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-yellow-200 text-sm font-medium mb-2">Special Price</p>
                  <p className="text-3xl font-bold text-white">$12</p>
                  <p className="text-yellow-200/80 text-xs">One-time payment for 30 days</p>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/coupons')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Purchase Access Now
                </button>
                
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all duration-300 border border-white/20"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Styles */}
        <style jsx>{`
          @keyframes twinkle {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.2); }
          }
          
          .animate-twinkle {
            animation: twinkle 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  // ========= UI (unchanged) =========
  return (
    <div className="h-screen bg-black text-white flex overflow-hidden">
      {/* Conversations List */}
      <div className={`${isMobile ? (showConversationList ? 'w-full' : 'hidden') : 'w-80'} bg-gray-900 border-r border-yellow-500/20 flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-yellow-500/20 bg-black">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Link to="/dashboard" className="flex items-center text-yellow-400 hover:text-yellow-300 group transition-colors">
                <div className="p-2 rounded-full bg-yellow-400/20 group-hover:bg-yellow-400/30 mr-2 transition-colors">
                  <FaArrowLeft className="w-4 h-4" />
                </div>
                {!isMobile && <span className="font-medium">Back</span>}
              </Link>

              <div>
                <h1 className="text-xl font-bold text-white flex items-center">
                  <FaComments className="w-5 h-5 text-yellow-400 mr-2" />
                  Messages
                </h1>
                {totalUnread > 0 && (
                  <span className="inline-flex items-center bg-yellow-400 text-black text-xs px-2 py-1 rounded-full font-bold mt-1">
                    {totalUnread} new
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <div className={`relative transition-all duration-300 ${isSearchFocused ? 'transform scale-105' : ''}`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className={`w-4 h-4 transition-colors duration-300 ${isSearchFocused ? 'text-yellow-400' : 'text-gray-400'}`} />
              </div>

              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search conversations..."
                className="w-full pl-10 pr-10 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-300"
              />

              {typingText && (
                <div className="absolute inset-y-0 left-10 flex items-center pointer-events-none">
                  <span className="text-yellow-400 font-medium">
                    {typingText}
                    <span className="inline-block w-0.5 h-4 bg-yellow-400 ml-1 animate-pulse" />
                  </span>
                </div>
              )}

              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <FaTimes className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-800">
                  <Skeleton circle width={48} height={48} baseColor="#374151" highlightColor="#4B5563" />
                  <div className="flex-1">
                    <Skeleton height={16} width="70%" baseColor="#374151" highlightColor="#4B5563" />
                    <Skeleton height={14} width="50%" baseColor="#374151" highlightColor="#4B5563" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredConversations.length > 0 ? (
            <div className="divide-y divide-gray-800">
              {filteredConversations.map((c) => (
                <button
                  key={c.friend_id}
                  onClick={() => selectConversation(c)}
                  className={`w-full p-4 text-left hover:bg-gray-800 transition-all duration-200 ${selectedConversation?.friend_id === c.friend_id ? 'bg-yellow-400/10 border-r-4 border-yellow-400' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-700 border-2 border-yellow-400/30">
                        {c.friend_profile_image
                          ? <img src={c.friend_profile_image} alt="Profile" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center bg-yellow-400 text-black font-bold">{c.friend_email.charAt(0).toUpperCase()}</div>}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-black ${c.friend_is_online ? 'bg-green-400' : 'bg-gray-500'}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-white truncate">
                          {c.friend_full_name || c.friend_email}
                        </h3>
                        <div className="flex items-center space-x-2">
                          {c.last_message_time && <span className="text-xs text-gray-400">{formatTime(c.last_message_time)}</span>}
                          {c.unread_count > 0 && <span className="bg-yellow-400 text-black text-xs px-2 py-1 rounded-full font-bold">{c.unread_count}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-gray-400 truncate mt-1">
                        {c.last_message_sender === currentUser?.id ? 'You: ' : ''}
                        {c.last_message || 'No messages yet'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUser className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">{searchQuery ? 'No conversations found' : 'No conversations yet'}</h3>
              <p className="text-gray-400 text-sm mb-4">{searchQuery ? `No conversations match "${searchQuery}"` : 'Start chatting with your friends!'}</p>
              {!searchQuery && (
                <Link to="/friends" className="inline-flex items-center px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-colors font-medium">
                  <FaUserFriends className="w-4 h-4 mr-2" />
                  View Friends
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${isMobile ? (showConversationList ? 'hidden' : 'w-full') : 'flex-1'} flex flex-col`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-yellow-500/20 bg-black">
              <div className="flex items-center justify-between">
                {/* Left */}
                <div className="flex items-center space-x-3">
                  {isMobile && (
                    <button onClick={() => setShowConversationList(true)} className="p-2 rounded-full hover:bg-gray-800 transition-colors">
                      <FaArrowLeft className="w-4 h-4 text-yellow-400" />
                    </button>
                  )}

                  <div className="relative flex items-center space-x-3">
                    <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full overflow-hidden bg-gray-700 border-2 border-yellow-400/50`}>
                      {selectedConversation.friend_profile_image
                        ? <img src={selectedConversation.friend_profile_image} alt="Profile" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center bg-yellow-400 text-black font-bold">
                            {selectedConversation.friend_email.charAt(0).toUpperCase()}
                          </div>}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 ${isMobile ? 'w-2 h-2' : 'w-3 h-3'} rounded-full border-2 border-black ${selectedConversation.friend_is_online ? 'bg-green-400' : 'bg-gray-500'}`} />

                    {!isMobile ? (
                      <div>
                        <h2 className="text-lg font-semibold text-white">
                          {selectedConversation.friend_full_name || selectedConversation.friend_email}
                        </h2>
                        <div className="flex items-center space-x-2">
                          <FaCircle className={`w-2 h-2 ${selectedConversation.friend_is_online ? 'text-green-400' : 'text-gray-500'}`} />
                          <p className="text-sm text-gray-400">{selectedConversation.friend_is_online ? 'Online' : 'Offline'}</p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h2 className="text-sm font-semibold text-white truncate max-w-[120px]">
                          {selectedConversation.friend_full_name?.split(' ')[0] || selectedConversation.friend_email.split('@')[0]}
                        </h2>
                        <div className="flex items-center space-x-1">
                          <FaCircle className={`w-1.5 h-1.5 ${selectedConversation.friend_is_online ? 'text-green-400' : 'text-gray-500'}`} />
                          <p className="text-xs text-gray-400">{selectedConversation.friend_is_online ? 'Online' : 'Offline'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: language */}
                <div className="flex items-center space-x-2">
                  {!isMobile && <span className="text-sm text-gray-400">Speaking:</span>}
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value as any)}
                    className={`bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 ${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}`}
                  >
                    <option value="english">{isMobile ? '🇺🇸' : '🇺🇸 English'}</option>
                    <option value="french">{isMobile ? '🇫🇷' : '🇫🇷 French'}</option>
                    <option value="haitian_creole">{isMobile ? '🇭🇹' : '🇭🇹 Haitian Creole'}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-900" style={{ height: 'calc(100vh - 140px)' }}>
              {messagesLoading ? (
                <div className="flex justify-center items-center h-full">
                  <FaSpinner className="w-8 h-8 text-yellow-400 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaComments className="w-10 h-10 text-yellow-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">Start a conversation</h3>
                  <p className="text-gray-400">
                    Send your first message to {selectedConversation.friend_full_name || selectedConversation.friend_email}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className={`flex ${message.sender_id === currentUser?.id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] sm:max-w-[70%] ${message.sender_id === currentUser?.id ? 'order-2' : 'order-1'}`}>
                        <div className={`flex items-center space-x-2 mb-2 ${message.sender_id === currentUser?.id ? 'justify-end' : 'justify-start'}`}>
                          <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700">
                            {message.sender_profile_image
                              ? <img src={message.sender_profile_image} alt="User" className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white">
                                  {message.sender_email?.charAt(0).toUpperCase()}
                                </div>}
                          </div>
                          <span className="text-xs text-gray-400">{message.sender_email}</span>
                          <span className="text-xs">{getLanguageFlag(message.original_language)}</span>
                          <span className="text-xs text-gray-500">{formatMessageTime(message.created_at)}</span>
                        </div>

                        <div className="space-y-2">
                          {message.spanish_translation && (
                            <div className={`p-4 rounded-2xl shadow-lg ${message.sender_id === currentUser?.id ? 'bg-yellow-400 text-black rounded-br-md' : 'bg-white text-black rounded-bl-md'}`}>
                              <div className="flex items-center space-x-2 mb-2">
                                <FaLanguage className="w-3 h-3 opacity-70" />
                                <span className="text-xs font-medium opacity-70">🇪🇸 Spanish:</span>
                              </div>
                              <p className="font-medium leading-relaxed">{message.spanish_translation}</p>
                            </div>
                          )}

                          <div className={`p-2 rounded-lg text-xs ${message.sender_id === currentUser?.id ? 'bg-yellow-400/20 text-yellow-200' : 'bg-gray-800 text-gray-300'}`}>
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium opacity-80">
                                {getLanguageFlag(message.original_language)} {getLanguageLabel(message.original_language)}:
                              </span>
                            </div>
                            <p className="opacity-90 leading-relaxed">{message.content}</p>
                          </div>
                        </div>

                        {message.sender_id === currentUser?.id && (
                          <div className="mt-1 flex items-center justify-end space-x-1">
                            <span className="text-xs text-gray-500">{formatMessageTime(message.created_at)}</span>
                            <div className="text-xs">
                              {message.is_read ? <FaCheckDouble className="w-3 h-3 text-yellow-400" title="Read" /> : <FaCheck className="w-3 h-3 text-gray-400" title="Sent" />}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {translating && (
                    <div className="flex justify-center">
                      <div className="flex items-center space-x-3 bg-gray-800 rounded-lg p-3">
                        <FaSpinner className="w-4 h-4 text-yellow-400 animate-spin" />
                        <span className="text-white text-sm">Translating to Spanish...</span>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-yellow-500/20 bg-black">
              <form onSubmit={sendMessage} className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Type in ${getLanguageLabel(selectedLanguage)}...`}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200"
                    disabled={sending || translating}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-sm text-gray-400">{getLanguageFlag(selectedLanguage)}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={sending || translating || !newMessage.trim()}
                  className="p-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {sending || translating ? <FaSpinner className="w-5 h-5 animate-spin" /> : <FaPaperPlane className="w-5 h-5" />}
                </button>
              </form>

              <div className="mt-2 text-center">
                <p className="text-xs text-gray-400 flex items-center justify-center space-x-2">
                  <FaGlobe className="w-3 h-3 text-yellow-400" />
                  <span>Message will be translated to Spanish automatically</span>
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-900">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaUser className="w-12 h-12 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Select a conversation</h3>
              <p className="text-gray-400">Choose a friend to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  ); 
}